import React, { useEffect, useMemo, useState } from "react";
import { ScrollView, View, StyleSheet, Pressable, TextInput } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { unionService } from "@/services/unionService";
import { UnionType } from "@/types/db/unions";
import useAuth from "@/hooks/useAuth";
import useToast from "@/hooks/useToast";
import { getEntityPermissions } from "@/lib/permissions";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";
import { router } from "expo-router";
import { useSettingsSearch } from "@/contexts/SettingsSearchContext";

export default function UnionsSettingsPage() {
  const { userRoles, loading: authLoading } = useAuth();
  const { colors } = useTheme();
  const toast = useToast();
  const [unions, setUnions] = useState<UnionType[]>([]);
  const [loading, setLoading] = useState(true);
  const { searchValue, setSearchValue, searchFocused, setSearchFocused } = useSettingsSearch("unions");

  const filteredUnions = useMemo(() => {
    if (!searchValue.trim()) return unions;
    const search = searchValue.toLowerCase();
    return unions.filter(
      (item) =>
        (item.name as string)?.toLowerCase().includes(search) || item.description?.toLowerCase().includes(search),
    );
  }, [unions, searchValue]);

  // Get permissions for unions
  const permissions = getEntityPermissions(userRoles, "unions");

  const fetchUnions = async () => {
    setLoading(true);
    try {
      const data = await unionService.getUnions();
      setUnions(data || []);
    } catch (error: any) {
      toast.showToast({
        title: "Failed to load unions",
        message: error?.message || "Unable to fetch unions",
        toastType: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Don't fetch if auth is still loading
    if (authLoading) return;

    if (permissions.read) {
      fetchUnions();
    } else {
      // If no read permission, stop loading
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions.read, authLoading]);

  const handleDeleteUnion = async (unionId: string) => {
    try {
      await unionService.deleteUnionsByIds([unionId]);
      setUnions(unions.filter((u) => u.id !== unionId));

      toast.showToast({
        title: "Union deleted",
        message: "Union deleted successfully",
        toastType: "success",
      });
    } catch (error: any) {
      toast.showToast({
        title: "Failed to delete union",
        message: error?.message || "Unable to delete union",
        toastType: "error",
      });
    }
  };

  // If no read permission, show error
  if (!permissions.read) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <MaterialCommunityIcons name="lock" size={48} color={colors.textSecondary} />
        <ThemedText type="defaultSemiBold" style={{ marginTop: 16 }}>
          Access Denied
        </ThemedText>
        <ThemedText style={{ opacity: 0.7, marginTop: 8 }}>You don&apos;t have permission to view unions</ThemedText>
      </ScrollView>
    );
  }

  if (authLoading || loading) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <ThemedText>{authLoading ? "Loading authentication..." : "Loading unions..."}</ThemedText>
      </ScrollView>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <View>
            <ThemedText type="title">Unions Management</ThemedText>
            <ThemedText style={{ opacity: 0.7, marginTop: 4 }}>Manage union memberships</ThemedText>
          </View>

          {/* Search */}
          <View style={[styles.searchContainer, { borderBottomColor: searchFocused ? colors.primary : colors.border }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={20}
              color={searchFocused ? colors.primary : colors.textSecondary}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.searchInput, { color: colors.text, outlineStyle: "none" } as any]}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")}>
                <MaterialCommunityIcons name="close-circle" size={18} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>

        {permissions.create && (
          <Button
            text="New Union"
            onPress={() => router.push("/settings/unions/new")}
            style={[styles.addButton, { backgroundColor: colors.green.background }]}
            textStyle={{ color: "white" }}
          />
        )}
      </View>

      {/* Unions List */}
      <ScrollView>
        {unions.length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="account-group-outline" size={64} color={colors.textSecondary} />
            <ThemedText style={{ marginTop: 16, opacity: 0.7 }}>No unions configured</ThemedText>
            {permissions.create && (
              <Button
                text="Create First Union"
                onPress={() => router.push("/settings/unions/new")}
                style={{ marginTop: 16 }}
              />
            )}
          </View>
        ) : (
          <View style={styles.unionsList}>
            {filteredUnions.map((union) => (
              <Pressable
                key={union.id as string}
                style={[
                  styles.unionCard,
                  {
                    backgroundColor: colors.card,
                    borderColor: colors.border,
                  },
                ]}
                onPress={() => router.push(`/settings/unions/${union.id}`)}
              >
                <View style={styles.unionHeader}>
                  <View style={styles.unionInfo}>
                    <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
                      {union.name as string}
                    </ThemedText>
                    {union.description && (
                      <ThemedText style={{ opacity: 0.6, marginTop: 4 }}>{union.description}</ThemedText>
                    )}
                    <ThemedText style={{ opacity: 0.6, marginTop: 8, fontSize: 12 }}>
                      {(union as any).unions_users?.length || 0} members
                    </ThemedText>
                  </View>
                  {permissions.delete && (
                    <Pressable style={styles.menuButton} onPress={() => handleDeleteUnion(union.id as string)}>
                      <MaterialCommunityIcons name="delete" size={20} color={colors.red?.text || colors.text} />
                    </Pressable>
                  )}
                </View>
              </Pressable>
            ))}
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    paddingBottom: 16,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
    flex: 1,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 6,
    borderBottomWidth: 1,
    width: 200,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    padding: 0,
  },
  addButton: {
    paddingHorizontal: 8,
    height: 35,
  },
  emptyState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 48,
  },
  unionsList: {
    gap: 12,
  },
  unionCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  unionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  unionInfo: {
    flex: 1,
  },
  menuButton: {
    padding: 4,
  },
});
